import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './VendorProfile.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const VendorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('marilink_token');

  useEffect(() => {
    const fetchVendor = async () => {
      try {
        const token = getToken();
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}/users/vendor/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();

        if (data.success) {
          setVendor(data.vendor);
        } else {
          setError(data.message || 'Vendor not found');
        }
      } catch (err) {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchVendor();
  }, [id]);

  const handleContact = () => {
    navigate(`/messages/${vendor._id}`, { 
      state: { userName: vendor.company || vendor.name } 
    });
  };

  if (loading) return <div className="loader-container"><i className="fa-solid fa-spinner fa-spin"></i></div>;
  if (error) return <div className="vp-wrapper" style={{textAlign: 'center'}}>{error}</div>;
  if (!vendor) return <div className="vp-wrapper" style={{textAlign: 'center'}}>No vendor data found</div>;

  return (
    <div className="vp-wrapper">
      <button className="vp-btn-back" onClick={() => navigate(-1)}>
        <i className="fa-solid fa-arrow-left"></i> Back
      </button>

      <div className="vp-header-card">
        <div className="vp-avatar">
          {(vendor.company || vendor.name || '?').charAt(0)}
        </div>
        <div className="vp-info">
          <h1>
            {vendor.company || vendor.name}
            {vendor.isVerified ? (
              <span className="vp-badge-verified"><i className="fa-solid fa-check"></i> Verified</span>
            ) : (
              <span className="vp-badge-pending"><i className="fa-solid fa-clock"></i> Pending</span>
            )}
          </h1>
          
          <div className="vp-rating">
            <span className="vp-stars">
              {'★'.repeat(Math.floor(vendor.rating || 5))}
              {'☆'.repeat(5 - Math.floor(vendor.rating || 5))}
            </span>
            <span>{vendor.rating || '5.0'} Rating</span>
            <span>•</span>
            <span>{vendor.completedJobs || 0} jobs completed</span>
          </div>

          <button className="vp-btn-message" onClick={handleContact}>
            <i className="fa-solid fa-envelope"></i> Message Vendor
          </button>
        </div>
      </div>

      <div className="vp-stats-grid">
        <div className="vp-stat-box">
          <span className="vp-stat-val">{new Date(vendor.createdAt).getFullYear()}</span>
          <span className="vp-stat-lbl">Member Since</span>
        </div>
        <div className="vp-stat-box">
          <span className="vp-stat-val">{vendor.totalJobs || 0}</span>
          <span className="vp-stat-lbl">Total Jobs</span>
        </div>
        <div className="vp-stat-box">
          <span className="vp-stat-val">{vendor.completedJobs || 0}</span>
          <span className="vp-stat-lbl">Completed</span>
        </div>
        <div className="vp-stat-box">
          <span className="vp-stat-val">100%</span>
          <span className="vp-stat-lbl">Response Rate</span>
        </div>
      </div>

      <div className="vp-section">
        <h3 className="vp-section-title"><i className="fa-solid fa-building"></i> Company Information</h3>
        <div className="vp-info-grid">
          <div className="vp-info-item"><span className="vp-info-lbl">Company Name</span><span className="vp-info-val">{vendor.company || vendor.name}</span></div>
          <div className="vp-info-item"><span className="vp-info-lbl">Registration No.</span><span className="vp-info-val">{vendor.registrationNo || 'N/A'}</span></div>
          <div className="vp-info-item"><span className="vp-info-lbl">Website</span><span className="vp-info-val">{vendor.website || 'N/A'}</span></div>
          <div className="vp-info-item"><span className="vp-info-lbl">Location</span><span className="vp-info-val">{vendor.location || 'N/A'}</span></div>
        </div>
      </div>

      <div className="vp-section">
        <h3 className="vp-section-title"><i className="fa-solid fa-certificate"></i> Certifications</h3>
        <div className="vp-tags">
          {vendor.certifications?.length > 0 ? (
            vendor.certifications.map((cert, idx) => (
              <span key={idx} className="vp-tag"><i className="fa-solid fa-check"></i> {cert}</span>
            ))
          ) : (
            <span className="vp-tag" style={{borderColor: 'transparent'}}>No certifications listed</span>
          )}
        </div>
      </div>

      <div className="vp-section">
        <h3 className="vp-section-title"><i className="fa-solid fa-wrench"></i> Specializations</h3>
        <div className="vp-tags">
          {vendor.serviceCategories?.length > 0 ? (
            vendor.serviceCategories.map((spec, idx) => (
              <span key={idx} className="vp-tag">{spec}</span>
            ))
          ) : (
            <span className="vp-tag" style={{borderColor: 'transparent'}}>Not specified</span>
          )}
        </div>
      </div>

      <div className="vp-section">
        <h3 className="vp-section-title"><i className="fa-solid fa-location-dot"></i> Port Coverage</h3>
        <div className="vp-tags">
          {vendor.ports?.length > 0 ? (
            vendor.ports.map((port, idx) => (
              <span key={idx} className="vp-tag"><i className="fa-solid fa-anchor"></i> {port}</span>
            ))
          ) : (
            <span className="vp-tag" style={{borderColor: 'transparent'}}>No ports listed</span>
          )}
        </div>
      </div>

      <div className="vp-section">
        <h3 className="vp-section-title"><i className="fa-solid fa-align-left"></i> About Vendor</h3>
        <p className="vp-desc">{vendor.description || 'No description provided by the vendor.'}</p>
      </div>
    </div>
  );
};

export default VendorProfile;